(() => {
  if (customElements.get('sd-tea-carousel')) return;
  customElements.define('sd-tea-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const track = this.querySelector('.sd-home-journal__track');
      const originals = [...track.children];
      const count = originals.length;
      if (count < 2) return;
      const HOLD_MS = 2300, SLIDE_MS = 700;
      const cloneCard = item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.teaClone = 'true';
        clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        clone.querySelectorAll('a,button,[tabindex]').forEach(el => el.tabIndex = -1);
        return clone;
      };
      // Copies on both sides permit a centred last-to-first transition without rewinding.
      const before = originals.map(cloneCard), after = originals.map(cloneCard);
      track.prepend(...before); track.append(...after);
      this.dataset.ready = 'true';
      this.dataset.holdMs = String(HOLD_MS);
      this.dataset.slideMs = String(SLIDE_MS);
      this.querySelector('.sd-home-journal__controls').hidden = false;
      const pause = this.querySelector('[data-tea-pause]');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const events = new AbortController();
      const on = (el, type, handler, options = {}) => el.addEventListener(type, handler, {...options, signal: events.signal});
      const modulo = value => ((value % count) + count) % count;
      let index = 0, step = 0, centreInset = 0, offset = 0;
      let held = 0, previous = 0, raf, animation = null, inView = false;
      let stopped = false, hovered = false, focused = false, drag = null, dragged = false;
      const target = i => (count + i) * step - centreInset;
      const draw = () => track.style.setProperty('--sd-tea-offset', offset.toFixed(3));
      const finish = i => {
        index = modulo(i); animation = null; held = 0;
        offset = target(index); draw();
        this.dataset.activeIndex = String(index);
        this.dataset.phase = 'holding';
      };
      const measure = () => {
        step = originals[1].getBoundingClientRect().left - originals[0].getBoundingClientRect().left;
        centreInset = (track.clientWidth - originals[0].getBoundingClientRect().width) / 2;
        finish(index);
      };
      const moveTo = (i, manual = false) => {
        if (animation) finish(animation.toIndex);
        if (motion.matches) { finish(i); return; }
        animation = {from: offset, to: target(i), toIndex: i, elapsed: 0, manual};
        held = 0; this.dataset.phase = 'moving';
      };
      const syncCounts = () => originals.forEach((item, i) => {
        const source = item.querySelector('[data-sd-views-id]');
        if (!source) return;
        [before[i], after[i]].forEach(card => {
          const node = card.querySelector('[data-sd-views-id]');
          if (node) { node.innerHTML = source.innerHTML; node.hidden = source.hidden; }
        });
      });
      const countsObserver = new MutationObserver(syncCounts);
      originals.forEach(item => countsObserver.observe(item, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden']}));
      syncCounts();
      const visibility = new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting; previous = 0;
      }, {threshold: .05});
      visibility.observe(track);
      const resize = new ResizeObserver(measure);
      resize.observe(track);
      const nextSlide = direction => {
        if (animation) finish(animation.toIndex);
        moveTo(index + direction, true);
      };
      on(this.querySelector('[data-tea-prev]'), 'click', () => nextSlide(-1));
      on(this.querySelector('[data-tea-next]'), 'click', () => nextSlide(1));
      on(pause, 'click', () => {
        stopped = !stopped; held = 0;
        pause.textContent = stopped ? 'Play' : 'Pause';
        pause.setAttribute('aria-label', stopped ? 'Start article scrolling' : 'Pause article scrolling');
        pause.setAttribute('aria-pressed', String(stopped));
      });
      on(track, 'mouseenter', () => { hovered = true; held = 0; });
      on(track, 'mouseleave', () => { hovered = false; held = 0; });
      on(track, 'focusin', event => {
        focused = true; held = 0;
        const i = originals.indexOf(event.target.closest('.sd-journal__card'));
        if (i >= 0 && i !== index) { track.scrollLeft = 0; finish(i); }
      });
      on(track, 'focusout', event => { focused = track.contains(event.relatedTarget); held = 0; });
      on(track, 'pointerdown', event => {
        if (event.button !== 0) return;
        animation = null; held = 0;
        drag = {x: event.clientX, y: event.clientY, offset, id: event.pointerId}; dragged = false;
      });
      on(track, 'pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!dragged && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          dragged = true; track.setPointerCapture(event.pointerId);
        }
        if (dragged) { offset = drag.offset - dx; draw(); }
      });
      const endDrag = () => {
        if (!drag) return;
        const nearest = Math.round((offset + centreInset) / step - count);
        drag = null; held = 0;
        moveTo(nearest, true);
      };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(track, 'click', event => {
        if (dragged) { event.preventDefault(); event.stopPropagation(); dragged = false; }
      }, {capture: true});
      const tick = time => {
        const elapsed = previous ? Math.min(time - previous, 50) : 0;
        previous = time;
        if (inView && !document.hidden && !drag) {
          if (animation && (!stopped || animation.manual)) {
            animation.elapsed += elapsed;
            const progress = Math.min(animation.elapsed / SLIDE_MS, 1);
            const ease = (1 - Math.cos(Math.PI * progress)) / 2;
            offset = animation.from + (animation.to - animation.from) * ease; draw();
            if (progress === 1) finish(animation.toIndex);
          } else if (!animation && !stopped && !motion.matches && !hovered && !focused) {
            held += elapsed;
            if (held >= HOLD_MS) moveTo(index + 1);
          }
        }
        raf = requestAnimationFrame(tick);
      };
      measure();
      raf = requestAnimationFrame(tick);
      this.cleanup = () => {
        cancelAnimationFrame(raf); events.abort(); visibility.disconnect(); resize.disconnect(); countsObserver.disconnect();
        [...before, ...after].forEach(clone => clone.remove());
        track.style.removeProperty('--sd-tea-offset');
        this.querySelector('.sd-home-journal__controls').hidden = true;
        delete this.dataset.ready; this.cleanup = null;
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
})();
